import React, { useState, useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Alert, Divider, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';

import ReportListExportSession from './ReportListExportSection';
import YtdReport from '../../../types/reports/ytdReport';
import LegislativeRaceTable from './LegislativeRaceTable';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const LegislativeRaces = (): JSX.Element => {
    
    document.title = 'OCPF - Legislative Races';

    const { showBoundary } = useErrorBoundary(); 
    const [isLoading, setIsLoading] = useState(true); 

    //generate dropdown years for every two years from 2020 (started using the depository system) to current year
    const dropDownYears = Array.from(new Array(Math.floor((new Date().getFullYear()-2019)/2)+1), (val, index) => new Date().getFullYear()%2===0?new Date().getFullYear()- index*2: new Date().getFullYear()-1 - index*2);
    const [reportYear, setReportYear] = useState(()=>{
        // return current year if current month is June or later, otherwise return previous even year.
        if(new Date().getFullYear()%2===0){
            return new Date().getMonth() < 5 ? new Date().getFullYear()-2: new Date().getFullYear();
        }
        return new Date().getFullYear()-1;
    });
    
    const [displayGroup, setDisplayGroup] = useState('HOUSE');
    const [reports, setReports] = useState<YtdReport[]>();
    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };

  const updateDisplayGroup = (event: any) => {
    setDisplayGroup(event.target.value);
  }


  useEffect(() => {
    getLegislativeRaceData();
  }, [reportYear, displayGroup])


  const getLegislativeRaceData = async () => {

    setIsLoading(false);
    setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/legislative/race/excelOutput/${reportYear}?displayGroup=${displayGroup}`);
    setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/legislative/race/textOutput/${reportYear}?displayGroup=${displayGroup}`);

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/legislative/race/depository/${reportYear}?displayGroup=${displayGroup}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the legislative race reports.'));
          }
          return result.json();
        })
        .then(result => {
          setReports(result);
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Legislative Races
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={{ xs: 12, md: 10 }}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel htmlFor="year-select">Year</InputLabel>
              <Select
                sx={{ fontSize: 'smaller' }}
                label="Year"
                value={reportYear}
                aria-labelledby='year-id'
                inputProps={{
                  id: 'year-select'
                }}
                onChange={updateReporYear}
              >
                {
                  dropDownYears.map((year) =>
                    <MenuItem value={year} key={year} >{year} All Legislative</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
              <InputLabel htmlFor="group-select">Group</InputLabel>
              <Select
                sx={{ fontSize: 'smaller' }}
                label="Group"
                inputProps={{
                  id: 'group-select'
                }}
                value={displayGroup}
                onChange={updateDisplayGroup}
              >
                <MenuItem value={'HOUSE'}>House - ALL</MenuItem>
                <MenuItem value={'BBB'}>House - Barnstable, Berkshire and Bristol</MenuItem>
                <MenuItem value={'NORTHANDWEST'}>House - Essex, Franklin, Hampden and Hampshire</MenuItem>
                <MenuItem value={'MIDDLESEX'}>House - Middlesex</MenuItem>
                <MenuItem value={'SOUTH'}>House - Norfolk and Plymouth</MenuItem>
                <MenuItem value={'SUFF-WORC'}>House - Suffolk and Worcester</MenuItem>
                <MenuItem value={'SENATE'}>Senate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
          </Grid>
          <Grid size={12} marginTop={2}>
            {!isLoading && reports?.length === 0 && (
              <Alert severity="warning" icon={false} sx={{ justifyContent: 'center' }}>
                No data is currently available for this race. On-ballot information may not yet be filed for this election cycle. Please check back later.
              </Alert>
            )}
          </Grid>
          <Grid size={12} marginTop={3}>
            <LegislativeRaceTable reports={reports}></LegislativeRaceTable>
          </Grid>
        </Grid>
        {/*loading indicator*/}
        <CircularProgress
          aria-label='Loading'
          size={50}
          className={isLoading ? "" : "hidden"}
          sx={{
            position: "fixed",
            left: "50%",
            top: "20%",
            transform: "translate(-50%, -50%)",
            zIndex: 2
          }}
        />
      </Container>
    </Main>
  );
};

export default LegislativeRaces;
